/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { LogWarning } from 'utils/loggingHelpers';
import { LocalStorageType } from './SurveyLocalStorageTypes';

export const GetLocalStorageKey = (surveyId: string) => `ENGAGE_LOCAL_STORAGE_${surveyId}`;

interface SetLocalStorageProps {
  survey: any;
  manifest: any;
  currentScreen: any;
  screenHistory: any;
  payload: any;
  submissionUuid: string;
  linkToken?: string;
  ownerSubject?: string;
  isComplete?: boolean;
  responseIdMap?: any;
  fingerprint: string;
}
export const SetLocalStorage = (props: SetLocalStorageProps) => {
  const {
    manifest,
    survey,
    currentScreen,
    screenHistory,
    payload,
    submissionUuid,
    linkToken,
    ownerSubject,
    isComplete,
    responseIdMap,
    fingerprint,
  } = props;
  try {
    if (!localStorage) return;

    const surveyData = {
      surveyId: survey?.structure?.id,
      surveyVersion: manifest?.version,
      answers: payload?.answers,
      currentScreen,
      screenHistory,
      submissionUuid,
      linkToken,
      ownerSubject,
      isComplete,
      responseIdMap,
      fingerprint,
      lastUpdated: new Date().toISOString(),
    };
    // reduce local storage size if already complete
    if (isComplete) {
      delete surveyData.answers;
      delete surveyData.currentScreen;
      delete surveyData.screenHistory;
      delete surveyData.responseIdMap;
    }
    const localStorageKey = GetLocalStorageKey(survey?.structure?.id || '');
    localStorage.setItem(localStorageKey, JSON.stringify(surveyData));
  } catch {
    // suppress error if user blocks local storage
    LogWarning('Exception in SetLocalStorage');
  }
};

interface SetLocalStorageAnswersOnlyProps {
  survey: any;
  payload: any;
}
export function SetLocalStorageAnswersOnly(props: SetLocalStorageAnswersOnlyProps) {
  // update answers object but keep other fields as is for retry purposes
  try {
    const { survey, payload } = props;
    if (!localStorage) return;
    const localStorageKey = GetLocalStorageKey(survey?.structure?.id || '');
    const surveyData = localStorage.getItem(localStorageKey);
    if (!surveyData) return;
    const existingData = JSON.parse(surveyData) as LocalStorageType;
    if (!existingData) return;
    existingData.answers = payload?.answers;
    localStorage.setItem(localStorageKey, JSON.stringify(existingData));
  } catch {
    // suppress error if user blocks local storage
    LogWarning('Exception in SetLocalStorageAnswersOnly');
  }
  return null;
}

export function GetLocalStorage(surveyId: string) {
  try {
    if (!localStorage) return null;

    const localStorageKey = GetLocalStorageKey(surveyId);
    const surveyData = localStorage.getItem(localStorageKey);
    if (surveyData) {
      return JSON.parse(surveyData) as LocalStorageType;
    }
  } catch {
    // suppress error if user blocks local storage
    LogWarning('Exception in GetLocalStorage');
  }
  return null;
}

// Rewrites the save of one submission. A save that belongs to another submission is left alone, and
// nothing is written when the change returns no new save.
const updateStoredSubmission = (
  surveyId: string,
  submissionUuid: string,
  change: (save: LocalStorageType) => LocalStorageType | undefined,
  operation: string,
) => {
  try {
    if (!localStorage) return;
    const localStorageKey = GetLocalStorageKey(surveyId);
    const surveyData = localStorage.getItem(localStorageKey);
    if (!surveyData) return;
    const existingData = JSON.parse(surveyData) as LocalStorageType;
    if (existingData?.submissionUuid !== submissionUuid) return;
    const changed = change(existingData);
    if (changed) localStorage.setItem(localStorageKey, JSON.stringify(changed));
  } catch {
    // suppress error if user blocks local storage
    LogWarning(`Exception in ${operation}`);
  }
};

// Drops the link token saved with a submission once it is linked to a resident: the token proves
// ownership of the submission, so it is kept only while it is needed.
export const RemoveStoredLinkToken = (surveyId: string, submissionUuid: string) =>
  updateStoredSubmission(
    surveyId,
    submissionUuid,
    ({ linkToken, ...save }) => (linkToken === undefined ? undefined : save),
    'RemoveStoredLinkToken',
  );

// Records next to a submission the resident it belongs to.
export const SetStoredSubmissionOwner = (surveyId: string, submissionUuid: string, ownerSubject: string) =>
  updateStoredSubmission(
    surveyId,
    submissionUuid,
    (save) => (save.ownerSubject === ownerSubject ? undefined : { ...save, ownerSubject }),
    'SetStoredSubmissionOwner',
  );

// Removes everything saved for the survey, the submission's link token with it.
export const ClearLocalStorage = (surveyId: string) => {
  // used if user wants to restart a survey in kiosk mode.
  try {
    if (!localStorage) return;

    const localStorageKey = GetLocalStorageKey(surveyId);
    localStorage.removeItem(localStorageKey);
  } catch {
    // suppress error if user blocks local storage
    LogWarning('Exception in ClearLocalStorage');
  }
};

const GetLocalLiveStorageKey = (sessionId: string) => `ENGAGE_LOCAL_LIVE_STORAGE_${sessionId}`;

interface SetLocalLiveStorageProps {
  liveAnswerState: any;
  liveAnswers: any;
  sessionId: any;
}
export const SetLocalLiveStorage = (props: SetLocalLiveStorageProps) => {
  const { liveAnswerState, liveAnswers, sessionId } = props;
  try {
    if (!localStorage) return;

    const surveyData = {
      sessionId,
      liveAnswerState,
      liveAnswers,
      lastUpdated: new Date().toISOString(),
    };
    const localStorageKey = GetLocalLiveStorageKey(sessionId);
    localStorage.setItem(localStorageKey, JSON.stringify(surveyData));
  } catch {
    // suppress error if user blocks local storage
    LogWarning('Exception in SetLocalLiveStorage');
  }
};
export function GetLocalLiveStorage(sessionId: string) {
  try {
    if (!localStorage) return null;

    const localStorageKey = GetLocalLiveStorageKey(sessionId);
    const surveyData = localStorage.getItem(localStorageKey);
    if (surveyData) {
      return JSON.parse(surveyData) as LocalStorageType;
    }
  } catch {
    // suppress error if user blocks local storage
    LogWarning('Exception in GetLocalLiveStorage');
  }
  return null;
}
export const ClearLocalLiveStorage = (sessionId: string) => {
  // used if user wants to restart a survey in kiosk mode.
  try {
    if (!localStorage) return;

    const localStorageKey = GetLocalLiveStorageKey(sessionId);
    localStorage.removeItem(localStorageKey);
  } catch {
    // suppress error if user blocks local storage
    LogWarning('Exception in ClearLocalLiveStorage');
  }
};

// converting local storage key from pre 1.2.16 to new per engagement key
export const MigrateLegacyLocalStorage = (surveyId: string, sessionId: string) => {
  try {
    if (!localStorage) return;
    const oldData = localStorage.getItem('ENGAGE_LOCAL_STORAGE');
    if (oldData) {
      const parsedData = JSON.parse(oldData);
      if (parsedData.surveyId === surveyId) {
        parsedData.lastUpdated = new Date().toISOString();
        const newKey = GetLocalStorageKey(surveyId);
        localStorage.setItem(newKey, JSON.stringify(parsedData));
        localStorage.removeItem('ENGAGE_LOCAL_STORAGE');
      }
    }
  } catch {
    LogWarning('Exception in MigrateLegacyLocalStorage');
  }
  // migrate ENGAGE_LOCAL_LIVE_STORAGE (pre 1.2.16)
  try {
    if (!localStorage) return;
    if (!sessionId) return;
    const oldData = localStorage.getItem('ENGAGE_LOCAL_LIVE_STORAGE');
    if (oldData) {
      const parsedData = JSON.parse(oldData);
      if (parsedData.sessionId === sessionId) {
        parsedData.lastUpdated = new Date().toISOString();
        const newKey = GetLocalLiveStorageKey(sessionId);
        localStorage.setItem(newKey, JSON.stringify(parsedData));
        localStorage.removeItem('ENGAGE_LOCAL_LIVE_STORAGE');
      }
    }
  } catch {
    LogWarning('Exception in MigrateLegacyLocalLiveStorage');
  }
};

// cleanup usage from past
export const CleanupLocalStorage = (surveyId: string) => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith('ENGAGE_LOCAL_')) {
        // do not clear if self
        if (key !== GetLocalStorageKey(surveyId)) {
          const value = localStorage.getItem(key);
          if (value) {
            const localData = JSON.parse(value);
            const expires = new Date(Date.now() - 1000 * 60 * 60 * 24 * 14); // 14 days
            // eslint-disable-next-line max-depth
            if (!localData.lastUpdated || new Date(localData.lastUpdated) < expires) {
              localStorage.removeItem(key);
            }
          }
        }
      }
    });
  } catch {
    // suppress error if user blocks local storage
    LogWarning('Exception in CleanupLocalStorage');
  }
};
